/**
 * Invest Page - Private Money Lending Opportunities
 */

import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  TrendingUp, 
  Shield, 
  Clock, 
  DollarSign, 
  CheckCircle, 
  ArrowRight, 
  Building2, 
  Users, 
  FileCheck, 
  Phone,
  Calendar,
  Lock,
  PiggyBank,
  Target,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?auto=format&fit=crop&q=80";

const highlights = [
  {
    icon: TrendingUp,
    title: "8-12% Returns",
    description: "Earn competitive annual returns backed by real estate collateral",
    color: "#48d480",
  },
  {
    icon: Clock,
    title: "6-12 Month Terms",
    description: "Short-term investments with predictable timelines",
    color: "#e8821c",
  },
  {
    icon: DollarSign,
    title: "$100K - $300K",
    description: "Investment range designed for serious private lenders",
    color: "#48d480",
  },
  {
    icon: Shield,
    title: "Secured by Real Estate",
    description: "Every investment is backed by tangible property collateral",
    color: "#e8821c",
  },
];

const howItWorks = [
  {
    step: 1,
    title: "Book a Discovery Call",
    description: "Schedule a confidential call to discuss your investment goals and learn about current opportunities.",
    icon: Phone,
  },
  {
    step: 2,
    title: "Review Investment Opportunities",
    description: "We present pre-vetted deals with qualified borrowers and secured real estate collateral.",
    icon: FileCheck,
  },
  {
    step: 3,
    title: "Fund Your Investment",
    description: "Choose your investment amount ($100K-$300K) and complete the funding process.",
    icon: PiggyBank,
  },
  {
    step: 4,
    title: "Earn Returns",
    description: "Receive your principal plus 8-12% returns at the end of the 6-12 month term.",
    icon: TrendingUp,
  },
];

const whyInvest = [
  {
    icon: Building2,
    title: "Real Estate Backed",
    description: "All investments are secured by tangible real estate assets, providing a safety net for your capital.",
  },
  {
    icon: Users,
    title: "Pre-Qualified Borrowers",
    description: "We only work with thoroughly vetted borrowers who meet strict qualification criteria.",
  },
  {
    icon: Target,
    title: "Consistent Returns",
    description: "Our track record demonstrates consistent 8-12% annual returns for our private investors.",
  },
  {
    icon: Lock,
    title: "Transparent Process",
    description: "Full visibility into each deal including borrower profiles, property details, and exit strategies.",
  },
];

const faqs = [
  {
    question: "What is the minimum investment amount?",
    answer: "The minimum investment is $100,000 and the maximum is $300,000 per deal. This range is designed for serious private lenders who want meaningful returns on their capital.",
  },
  {
    question: "How are the investments secured?",
    answer: "Every investment is secured by real estate collateral. We only fund deals where the property value significantly exceeds the loan amount, providing a strong safety margin for investors.",
  },
  {
    question: "What is the typical investment term?",
    answer: "Investment terms typically range from 6 to 12 months. The exact term depends on the specific deal structure and borrower's business plan.",
  },
  {
    question: "How do I receive my returns?",
    answer: "Returns are paid at the conclusion of the investment term. You receive your full principal plus the agreed-upon interest (8-12% annually) when the borrower completes their exit.",
  },
  {
    question: "Who are the borrowers?",
    answer: "Our borrowers are experienced real estate investors and business owners who have been thoroughly vetted. We only work with qualified buyers who have proven track records and solid exit strategies.",
  },
  {
    question: "What happens if a borrower defaults?",
    answer: "In the rare event of default, the real estate collateral secures your investment. Our conservative loan-to-value ratios ensure there's sufficient equity to protect investor capital.",
  },
  {
    question: "Do I need to be an accredited investor?",
    answer: "While we welcome all serious investors, many of our opportunities are structured for accredited investors. During your discovery call, we'll discuss which opportunities best match your investor profile.",
  },
  {
    question: "How do I get started?",
    answer: "Simply book a discovery call using the form on this page. We'll discuss your investment goals, answer your questions, and present current opportunities that match your criteria.",
  },
];

export default function Invest() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    investmentAmount: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.name.split(' ')[0] || formData.name,
          lastName: formData.name.split(' ').slice(1).join(' ') || '',
          email: formData.email,
          phone: formData.phone,
          businessName: '',
          consultationType: 'Investor Discovery Call - Real Estate Lending',
          fundingRange: formData.investmentAmount,
          yearsInBusiness: '',
          message: formData.message
        })
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: "", email: "", phone: "", investmentAmount: "", message: "" });
      }
    } catch {
      // Form submission failed silently
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="invest-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden pt-24">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52] via-[#0f2d52]/90 to-[#0f2d52]/70" />
        
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="">
              <div className="inline-flex items-center gap-2 bg-[#48d480]/20 rounded-full px-4 py-2 mb-6">
                <TrendingUp size={16} className="text-[#48d480]" />
                <span className="text-[#48d480] text-sm font-bold">Private Money Lending</span>
              </div>
              
              <h1
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
              >
                Grow Your Wealth With{" "}
                <span className="text-[#48d480]">8-12% Returns</span>
              </h1>
              
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/80 text-lg mb-8 max-w-xl"
              >
                Become a private money investor and earn consistent returns backed by 
                real estate collateral. We connect you with pre-qualified borrowers and 
                secure investment opportunities.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-5 py-3 border border-white/20">
                  <div className="text-[#48d480] text-2xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>8-12%</div>
                  <div className="text-white/60 text-sm">Annual Returns</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-5 py-3 border border-white/20">
                  <div className="text-[#48d480] text-2xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>6-12</div>
                  <div className="text-white/60 text-sm">Month Terms</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-5 py-3 border border-white/20">
                  <div className="text-[#48d480] text-2xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>$100K+</div>
                  <div className="text-white/60 text-sm">Minimum Investment</div>
                </div>
              </div>

              <a href="#book-call" className="btn-primary inline-flex items-center gap-2">
                Book a Discovery Call <ArrowRight size={18} />
              </a>
            </div>

            {/* Stats Card */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Investment Overview
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-white/70">Annual Returns</span>
                    <span className="text-[#48d480] font-bold">8-12%</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-white/70">Investment Term</span>
                    <span className="text-white font-bold">6-12 Months</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-white/70">Minimum Investment</span>
                    <span className="text-white font-bold">$100,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-white/70">Maximum Investment</span>
                    <span className="text-white font-bold">$300,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-white/70">Collateral</span>
                    <span className="text-white font-bold">Real Estate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-all "
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <Icon size={24} style={{ color: item.color }} />
                  </div>
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-[#0f2d52] font-bold text-lg mb-2"
                  >
                    {item.title}
                  </h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Invest With Us */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-14 ">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="text-[#48d480] text-xs font-bold tracking-widest uppercase">Why Choose Us</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              Why Invest With <span className="text-[#48d480]">Legacy Triangle</span>
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-gray-500 max-w-2xl mx-auto"
            >
              We connect private investors with carefully vetted real estate opportunities, 
              providing consistent returns with the security of tangible collateral.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {whyInvest.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex gap-5 p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all "
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div className="w-14 h-14 rounded-xl bg-[#0f2d52] flex items-center justify-center flex-shrink-0">
                    <Icon size={28} className="text-[#48d480]" />
                  </div>
                  <div>
                    <h3
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                      className="text-[#0f2d52] font-bold text-lg mb-2"
                    >
                      {item.title}
                    </h3>
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="text-center mb-14 ">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="text-[#48d480] text-xs font-bold tracking-widest uppercase">The Process</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-white mb-4"
            >
              How It <span className="text-[#48d480]">Works</span>
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/60 max-w-2xl mx-auto"
            >
              Our streamlined process makes it easy to start earning returns on your investment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.step}
                  className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 "
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#48d480] flex items-center justify-center">
                    <span className="text-[#0f2d52] font-bold text-sm">{item.step}</span>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mb-4 mt-2">
                    <Icon size={24} className="text-[#48d480]" />
                  </div>
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-white font-bold text-lg mb-2"
                  >
                    {item.title}
                  </h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-sm">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Details */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#48d480]" />
                <span className="text-[#48d480] text-xs font-bold tracking-widest uppercase">Investment Details</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-6"
              >
                Secure, Transparent{" "}
                <span className="text-[#48d480]">Investments</span>
              </h2>
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-gray-500 mb-8"
              >
                Every investment opportunity is carefully structured to protect your capital 
                while delivering competitive returns. Here's what you can expect:
              </p>

              <div className="space-y-4">
                {[
                  "All deals secured by real estate collateral",
                  "Conservative loan-to-value ratios (typically 65-75%)",
                  "Thorough borrower vetting and due diligence",
                  "Clear exit strategies for every deal",
                  "Regular updates on investment status",
                  "Full documentation and transparency",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#48d480] mt-0.5 flex-shrink-0" />
                    <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="">
              <div className="bg-[#0f2d52] rounded-2xl p-8">
                <h3
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-white font-bold text-2xl mb-6"
                >
                  Investment Structure
                </h3>
                
                <div className="space-y-6">
                  <div className="bg-white/5 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <DollarSign size={20} className="text-[#48d480]" />
                      <span className="text-white font-bold">Investment Range</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/60">Minimum</span>
                      <span className="text-[#48d480] font-bold text-xl">$100,000</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-white/60">Maximum</span>
                      <span className="text-[#48d480] font-bold text-xl">$300,000</span>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingUp size={20} className="text-[#48d480]" />
                      <span className="text-white font-bold">Expected Returns</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/60">Annual Rate</span>
                      <span className="text-[#48d480] font-bold text-xl">8-12%</span>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock size={20} className="text-[#48d480]" />
                      <span className="text-white font-bold">Investment Term</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/60">Duration</span>
                      <span className="text-[#48d480] font-bold text-xl">6-12 Months</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-[#48d480]/10 rounded-lg border border-[#48d480]/20">
                  <p className="text-white/80 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <strong className="text-[#48d480]">Example:</strong> A $100,000 investment at 12% 
                    for 12 months would yield $12,000 in returns, for a total payout of $112,000.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container max-w-3xl">
          <div className="text-center mb-14 ">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="text-[#48d480] text-xs font-bold tracking-widest uppercase">FAQ</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              Frequently Asked <span className="text-[#48d480]">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden "
                style={{ transitionDelay: `${idx * 50}ms` }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="font-bold text-[#0f2d52] pr-4"
                  >
                    {faq.question}
                  </span>
                  {openFaq === idx ? (
                    <ChevronUp size={20} className="text-[#48d480] flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5">
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book a Call Section */}
      <section id="book-call" className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#48d480]" />
                <span className="text-[#48d480] text-xs font-bold tracking-widest uppercase">Get Started</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-white mb-6"
              >
                Ready to <span className="text-[#48d480]">Grow Your Wealth?</span>
              </h2>
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/70 mb-8"
              >
                Book a confidential discovery call with our investment team. We'll discuss 
                your financial goals, answer your questions, and present current opportunities 
                that match your investment criteria.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#48d480]/20 flex items-center justify-center">
                    <Calendar size={18} className="text-[#48d480]" />
                  </div>
                  <span className="text-white">30-minute confidential call</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#48d480]/20 flex items-center justify-center">
                    <FileCheck size={18} className="text-[#48d480]" />
                  </div>
                  <span className="text-white">Review current investment opportunities</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#48d480]/20 flex items-center justify-center">
                    <Shield size={18} className="text-[#48d480]" />
                  </div>
                  <span className="text-white">No obligation, completely confidential</span>
                </div>
              </div>
            </div>

            <div className="">
              <div className="bg-white rounded-2xl p-8">
                <h3
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-[#0f2d52] font-bold text-xl mb-6"
                >
                  Book Your Discovery Call
                </h3>

                {submitSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-[#48d480]/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-[#48d480]" />
                    </div>
                    <h4 className="text-[#0f2d52] font-bold text-lg mb-2">Thank You!</h4>
                    <p className="text-gray-500">
                      We've received your request. A member of our investment team will 
                      contact you within 24 hours to schedule your discovery call.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Investment Amount Interest</label>
                      <select
                        value={formData.investmentAmount}
                        onChange={(e) => setFormData({ ...formData, investmentAmount: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
                      >
                        <option value="">Select an amount</option>
                        <option value="100000">$100,000</option>
                        <option value="150000">$150,000</option>
                        <option value="200000">$200,000</option>
                        <option value="250000">$250,000</option>
                        <option value="300000">$300,000</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
                        placeholder="Tell us about your investment goals..."
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? "Submitting..." : "Request Discovery Call"}
                      <ArrowRight size={18} />
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                      Your information is confidential and will never be shared.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="container">
          <p className="text-gray-500 text-xs text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
            <strong>Disclaimer:</strong> All investments carry risk. Past performance is not indicative of future results. 
            The information provided on this page is for informational purposes only and should not be considered investment advice. 
            Please consult with a qualified financial advisor before making any investment decisions.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
